/*
===============================================================================================================
                                 Copyright 2026-2026, Blue Yonder Group, Inc.
                                              All Rights Reserved

                                THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
                                            BLUE YONDER GROUP, INC.

                            The copyright notice above does not evidence any actual
                                 or intended publication of such source code.

===============================================================================================================
*/

import React, {
  createContext, useContext, useState, useEffect,
} from 'react';
import PropTypes from 'prop-types';
import luiPortalUtilitiesManager from '@/utilities/lui-portal-utilities-manager';
import { getPreferencesFromPortal } from '@/utilities/preferences';
import LoadingScreen from '@/components/loading-screen';
import LuminatePortalErrorScreen from '@/components/luminate-portal-error-screen';

const getDefaultPortalConfig = () => ({
  isPortal: false,
  hostId: null,
  companyId: null,
  applicationMode: null,
  getHostId: () => null,
  getCompanyId: () => null,
  getApplicationMode: () => null,
});

export const LuminatePortalConfigContext = createContext(getDefaultPortalConfig());

// Set as default host ID for the portal only if the portal has not set it in the preferences
const DEFAULT_HOST_ID = 'BLUE_YONDER';
const PREFERENCES_FETCH_TIMEOUT_MS = 10000;

const LuminatePortalConfigProvider = ({ children }) => {
  const [state, setState] = useState({
    loading: false,
    error: null,
    hostId: null,
    companyId: null,
    isPortal: false,
    applicationMode: null,
  });

  const isInPortal = typeof window !== 'undefined' && luiPortalUtilitiesManager.isInIFrame();

  useEffect(() => {
    if (!isInPortal) return () => {};

    setState((prevState) => ({ ...prevState, loading: true }));

    let cancelled = false;
    const timeoutId = setTimeout(() => {
      if (cancelled) return;
      setState((prevState) => ({
        ...prevState,
        loading: false,
        error: true,
        isPortal: false,
      }));
    }, PREFERENCES_FETCH_TIMEOUT_MS);

    getPreferencesFromPortal()
      .then((prefs) => {
        if (cancelled) return;
        clearTimeout(timeoutId);
        const { hostId: prefsHostId, companyId, applicationMode } = prefs;
        const hostId = prefsHostId ?? DEFAULT_HOST_ID;
        setState({
          loading: false,
          error: null,
          hostId,
          companyId,
          applicationMode,
          isPortal: true,
        });
      })
      .catch(() => {
        if (cancelled) return;
        clearTimeout(timeoutId);
        setState((prevState) => ({
          ...prevState,
          loading: false,
          error: true,
          isPortal: false,
        }));
      });

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [isInPortal]);

  if (!isInPortal) {
    return (
      <LuminatePortalConfigContext.Provider value={getDefaultPortalConfig()}>
        {children}
      </LuminatePortalConfigContext.Provider>
    );
  }

  if (state.loading) {
    return <LoadingScreen />;
  }

  if (state.error) {
    return <LuminatePortalErrorScreen />;
  }

  const portalConfig = {
    isPortal: true,
    hostId: state.hostId,
    companyId: state.companyId,
    applicationMode: state.applicationMode,
    getHostId: () => state.hostId,
    getCompanyId: () => state.companyId,
    getApplicationMode: () => state.applicationMode,
  };

  return (
    <LuminatePortalConfigContext.Provider value={portalConfig}>
      {children}
    </LuminatePortalConfigContext.Provider>
  );
};

LuminatePortalConfigProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useLuminatePortalConfig = () => useContext(LuminatePortalConfigContext);

export default LuminatePortalConfigProvider;
